import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Drawer,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AddRounded,
  CloseRounded,
  DeleteOutlineRounded,
  EditNoteRounded,
  SearchRounded,
} from '@mui/icons-material';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { collection, onSnapshot, query, DocumentData, getDocs } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useTenantStore } from '../../stores/tenant.store';
import { courseTemplateApi, type TextbookDto } from '../../lib/api/course-template.api';
import { useConfirm } from '../../components/ConfirmProvider';
import { errorText } from '../../lib/error-text';

type LangFilter = 'all' | 'es' | 'en' | 'pt' | 'other';
type UsageFilter = 'all' | 'unused' | 'used';
type SortKey = 'title' | 'year' | 'usage';
type SortDir = 'asc' | 'desc';

interface CatalogRow extends TextbookDto {
  usageCount: number;
}

// Maps stable language codes to i18n keys; labels resolved via t() at render.
const LANG_LABEL_KEY: Record<string, string> = {
  es: 'textbooks.lang.es',
  en: 'textbooks.lang.en',
  pt: 'textbooks.lang.pt',
  other: 'textbooks.lang.other',
};

export function TextbookCatalogPage(): JSX.Element {
  const { t } = useTranslation('sis');
  const confirm = useConfirm();
  const { currentTenantId } = useTenantStore();
  const [books, setBooks] = useState<TextbookDto[]>([]);
  const [usage, setUsage] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [search, setSearch] = useState('');
  const [langFilter, setLangFilter] = useState<LangFilter>('all');
  const [usageFilter, setUsageFilter] = useState<UsageFilter>('all');

  // Sorting + pagination
  const [sortKey, setSortKey] = useState<SortKey>('title');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Selection
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // Drawer
  const [editing, setEditing] = useState<TextbookDto | null>(null);
  const [creating, setCreating] = useState(false);

  // Live textbook subscription
  useEffect(() => {
    if (!currentTenantId) return;
    setLoading(true);
    const unsub = onSnapshot(
      query(collection(db, `tenants/${currentTenantId}/textbooks`)),
      (snap) => {
        const items: TextbookDto[] = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }) as TextbookDto & { deletedAt?: unknown })
          .filter((t) => !t.deletedAt)
          .map((t) => ({
            id: t.id,
            title: t.title,
            authors: t.authors ?? [],
            ...(t.publisher ? { publisher: t.publisher } : {}),
            ...(t.year ? { year: t.year } : {}),
            ...(t.isbn ? { isbn: t.isbn } : {}),
            language: t.language ?? 'es',
            category: t.category ?? 'required',
            ...(t.notes ? { notes: t.notes } : {}),
          }));
        setBooks(items);
        setLoading(false);
      },
      (e) => {
        setError(e.message);
        setLoading(false);
      },
    );
    return () => unsub();
  }, [currentTenantId]);

  // Compute usage from all course templates (one-shot)
  useEffect(() => {
    if (!currentTenantId) return;
    (async () => {
      try {
        const snap = await getDocs(collection(db, `tenants/${currentTenantId}/courseTemplates`));
        const counts: Record<string, number> = {};
        for (const docSnap of snap.docs) {
          const data = docSnap.data() as DocumentData;
          if (data['deletedAt']) continue;
          const readings = (data['readings'] ?? {}) as {
            required?: { textbookId: string }[];
            complementary?:
              | { textbookId: string }[]
              | { plan?: { textbookId: string }[] }
              | undefined;
          };
          const complementaryList = Array.isArray(readings.complementary)
            ? readings.complementary
            : (readings.complementary?.plan ?? []);
          const ids = [
            ...(Array.isArray(readings.required) ? readings.required : []).map((r) => r.textbookId),
            ...complementaryList.map((r) => r.textbookId),
          ].filter(Boolean);
          for (const id of ids) counts[id] = (counts[id] ?? 0) + 1;
        }
        setUsage(counts);
      } catch (e) {
        // non-fatal
        console.error('[textbook catalog] usage compute failed', e);
      }
    })();
  }, [currentTenantId, books.length]);

  // Filter + sort
  const rows: CatalogRow[] = useMemo(() => {
    const q = search.trim().toLowerCase();
    let arr: CatalogRow[] = books.map((b) => ({ ...b, usageCount: usage[b.id] ?? 0 }));
    if (q) {
      arr = arr.filter(
        (b) =>
          b.title.toLowerCase().includes(q) ||
          b.authors.some((a) => a.toLowerCase().includes(q)) ||
          (b.isbn ?? '').toLowerCase().includes(q),
      );
    }
    if (langFilter !== 'all') arr = arr.filter((b) => b.language === langFilter);
    if (usageFilter === 'unused') arr = arr.filter((b) => b.usageCount === 0);
    if (usageFilter === 'used') arr = arr.filter((b) => b.usageCount > 0);

    arr.sort((a, b) => {
      let cmp = 0;
      if (sortKey === 'title') cmp = a.title.localeCompare(b.title);
      else if (sortKey === 'year') cmp = (a.year ?? 0) - (b.year ?? 0);
      else if (sortKey === 'usage') cmp = a.usageCount - b.usageCount;
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return arr;
  }, [books, usage, search, langFilter, usageFilter, sortKey, sortDir]);

  const paginatedRows = rows.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const allSelected = paginatedRows.length > 0 && paginatedRows.every((r) => selected.has(r.id));
  const toggleAll = () => {
    const next = new Set(selected);
    if (allSelected) paginatedRows.forEach((r) => next.delete(r.id));
    else paginatedRows.forEach((r) => next.add(r.id));
    setSelected(next);
  };
  const toggleOne = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };

  const handleBulkDelete = async () => {
    if (!currentTenantId || selected.size === 0) return;
    const heavilyUsed = Array.from(selected).filter((id) => (usage[id] ?? 0) > 0);
    if (heavilyUsed.length > 0) {
      const ok = await confirm({
        message: t('textbooks.confirm.bulkDeleteInUse', {
          count: heavilyUsed.length,
          total: selected.size,
        }),
        confirmColor: 'error',
      });
      if (!ok) return;
    }
    setError(null);
    try {
      await Promise.all(
        Array.from(selected).map((id) =>
          courseTemplateApi.deleteTextbook({ tenantId: currentTenantId, textbookId: id }),
        ),
      );
      setSelected(new Set());
    } catch (e) {
      setError(errorText(e, t('textbooks.errors.delete')));
    }
  };

  if (loading) {
    return (
      <Box textAlign="center" py={8}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
        <Box>
          <Typography variant="h4" fontWeight={700}>
            {t('textbooks.title')}
          </Typography>
          <Typography color="text.secondary">
            {t('textbooks.subtitle', { count: books.length })}
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddRounded />} onClick={() => setCreating(true)}>
          {t('textbooks.actions.new')}
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
          <TextField
            size="small"
            placeholder={t('textbooks.search.placeholder')}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            sx={{ flex: 1, minWidth: 240 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRounded fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            size="small"
            select
            label={t('textbooks.fields.language')}
            value={langFilter}
            onChange={(e) => setLangFilter(e.target.value as LangFilter)}
            sx={{ minWidth: 140 }}
          >
            <MenuItem value="all">{t('textbooks.filters.all')}</MenuItem>
            <MenuItem value="es">{t('textbooks.lang.es')}</MenuItem>
            <MenuItem value="en">{t('textbooks.lang.en')}</MenuItem>
            <MenuItem value="pt">{t('textbooks.lang.pt')}</MenuItem>
            <MenuItem value="other">{t('textbooks.lang.other')}</MenuItem>
          </TextField>
          <TextField
            size="small"
            select
            label={t('textbooks.fields.usage')}
            value={usageFilter}
            onChange={(e) => setUsageFilter(e.target.value as UsageFilter)}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="all">{t('textbooks.filters.all')}</MenuItem>
            <MenuItem value="used">{t('textbooks.filters.used')}</MenuItem>
            <MenuItem value="unused">{t('textbooks.filters.unused')}</MenuItem>
          </TextField>
          {selected.size > 0 && (
            <Button
              size="small"
              variant="outlined"
              color="error"
              startIcon={<DeleteOutlineRounded />}
              onClick={handleBulkDelete}
            >
              {t('textbooks.actions.deleteN', { count: selected.size })}
            </Button>
          )}
        </Stack>
      </Paper>

      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={allSelected}
                  indeterminate={!allSelected && paginatedRows.some((r) => selected.has(r.id))}
                  onChange={toggleAll}
                />
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortKey === 'title'}
                  direction={sortDir}
                  onClick={() => toggleSort('title')}
                >
                  {t('textbooks.fields.title')}
                </TableSortLabel>
              </TableCell>
              <TableCell>{t('textbooks.fields.authors')}</TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortKey === 'year'}
                  direction={sortDir}
                  onClick={() => toggleSort('year')}
                >
                  {t('textbooks.fields.year')}
                </TableSortLabel>
              </TableCell>
              <TableCell>{t('textbooks.fields.publisher')}</TableCell>
              <TableCell>{t('textbooks.fields.language')}</TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortKey === 'usage'}
                  direction={sortDir}
                  onClick={() => toggleSort('usage')}
                >
                  {t('textbooks.fields.usedIn')}
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">{t('textbooks.fields.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    {books.length === 0
                      ? t('textbooks.empty.noBooks')
                      : t('textbooks.empty.noMatches')}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {paginatedRows.map((b) => (
              <TableRow key={b.id} hover selected={selected.has(b.id)}>
                <TableCell padding="checkbox">
                  <Checkbox checked={selected.has(b.id)} onChange={() => toggleOne(b.id)} />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight={600}>
                    {b.title}
                  </Typography>
                  {b.isbn && (
                    <Typography variant="caption" color="text.secondary">
                      {t('textbooks.fields.isbnLabel', { isbn: b.isbn })}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{b.authors.join(', ')}</Typography>
                </TableCell>
                <TableCell>{b.year ?? '—'}</TableCell>
                <TableCell>{b.publisher ?? '—'}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={t(LANG_LABEL_KEY[b.language] ?? '', { defaultValue: b.language })}
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  {b.usageCount === 0 ? (
                    <Chip
                      size="small"
                      label={t('textbooks.usage.unused')}
                      color="default"
                      variant="outlined"
                    />
                  ) : (
                    <Chip
                      size="small"
                      label={t('textbooks.usage.courseCount', { count: b.usageCount })}
                      color="primary"
                      variant="outlined"
                    />
                  )}
                </TableCell>
                <TableCell align="right">
                  <Tooltip title={t('textbooks.actions.edit')}>
                    <IconButton size="small" onClick={() => setEditing(b)}>
                      <EditNoteRounded fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={t('textbooks.actions.delete')}>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={async () => {
                        if (!currentTenantId) return;
                        const used = usage[b.id] ?? 0;
                        const ok = await confirm({
                          message:
                            used > 0
                              ? t('textbooks.confirm.deleteInUse', { count: used })
                              : t('textbooks.confirm.delete', { title: b.title }),
                          confirmColor: 'error',
                        });
                        if (!ok) return;
                        try {
                          await courseTemplateApi.deleteTextbook({
                            tenantId: currentTenantId,
                            textbookId: b.id,
                          });
                        } catch (e) {
                          setError(errorText(e, t('textbooks.errors.generic')));
                        }
                      }}
                    >
                      <DeleteOutlineRounded fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={rows.length}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </TableContainer>

      <TextbookDrawer
        open={!!editing || creating}
        textbook={editing}
        tenantId={currentTenantId}
        usageCount={editing ? (usage[editing.id] ?? 0) : 0}
        onClose={() => {
          setEditing(null);
          setCreating(false);
        }}
        onError={(m) => setError(m)}
      />
    </Box>
  );
}

interface DrawerProps {
  open: boolean;
  textbook: TextbookDto | null;
  tenantId: string | null;
  usageCount: number;
  onClose: () => void;
  onError: (msg: string) => void;
}

function TextbookDrawer({
  open,
  textbook,
  tenantId,
  usageCount,
  onClose,
  onError,
}: DrawerProps): JSX.Element {
  const { t } = useTranslation('sis');
  const [title, setTitle] = useState('');
  const [authors, setAuthors] = useState('');
  const [year, setYear] = useState('');
  const [publisher, setPublisher] = useState('');
  const [isbn, setIsbn] = useState('');
  const [language, setLanguage] = useState<'es' | 'en' | 'pt' | 'other'>('es');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (textbook) {
      setTitle(textbook.title);
      setAuthors(textbook.authors.join(', '));
      setYear(textbook.year ? String(textbook.year) : '');
      setPublisher(textbook.publisher ?? '');
      setIsbn(textbook.isbn ?? '');
      setLanguage(textbook.language);
      setNotes(textbook.notes ?? '');
    } else {
      setTitle('');
      setAuthors('');
      setYear('');
      setPublisher('');
      setIsbn('');
      setLanguage('es');
      setNotes('');
    }
  }, [textbook, open]);

  const handleSave = async () => {
    if (!tenantId) return;
    if (!title.trim()) {
      onError(t('textbooks.errors.titleRequired'));
      return;
    }
    const authorsArr = authors
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    if (authorsArr.length === 0) {
      onError(t('textbooks.errors.authorRequired'));
      return;
    }
    setSaving(true);
    try {
      if (textbook) {
        await courseTemplateApi.updateTextbook({
          tenantId,
          textbookId: textbook.id,
          updates: {
            title: title.trim(),
            authors: authorsArr,
            ...(year ? { year: Number(year) } : {}),
            ...(publisher.trim() ? { publisher: publisher.trim() } : {}),
            ...(isbn.trim() ? { isbn: isbn.trim() } : {}),
            language,
            ...(notes.trim() ? { notes: notes.trim() } : {}),
          },
        });
      } else {
        await courseTemplateApi.createTextbook({
          tenantId,
          title: title.trim(),
          authors: authorsArr,
          ...(year ? { year: Number(year) } : {}),
          ...(publisher.trim() ? { publisher: publisher.trim() } : {}),
          ...(isbn.trim() ? { isbn: isbn.trim() } : {}),
          language,
          ...(notes.trim() ? { notes: notes.trim() } : {}),
        });
      }
      onClose();
    } catch (e) {
      onError(errorText(e, t('textbooks.errors.save')));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: 460 } } }}
    >
      <Box sx={{ p: 3 }}>
        <Stack direction="row" alignItems="center" spacing={1} mb={2}>
          <Typography variant="h6" fontWeight={700} sx={{ flex: 1 }}>
            {textbook ? t('textbooks.drawer.editTitle') : t('textbooks.drawer.newTitle')}
          </Typography>
          <IconButton onClick={onClose}>
            <CloseRounded />
          </IconButton>
        </Stack>
        {textbook && usageCount > 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {t('textbooks.drawer.usageInfo', { count: usageCount })}
          </Alert>
        )}
        <Stack spacing={2}>
          <TextField
            label={t('textbooks.fields.title')}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            fullWidth
            autoFocus
          />
          <TextField
            label={t('textbooks.fields.authorsCsv')}
            value={authors}
            onChange={(e) => setAuthors(e.target.value)}
            required
            fullWidth
            helperText={t('textbooks.fields.authorsHelper')}
          />
          <Stack direction="row" spacing={1}>
            <TextField
              label={t('textbooks.fields.year')}
              type="number"
              value={year}
              onChange={(e) => setYear(e.target.value)}
              sx={{ width: 120 }}
            />
            <TextField
              label={t('textbooks.fields.publisher')}
              value={publisher}
              onChange={(e) => setPublisher(e.target.value)}
              sx={{ flex: 1 }}
            />
          </Stack>
          <TextField
            label={t('textbooks.fields.isbn')}
            value={isbn}
            onChange={(e) => setIsbn(e.target.value)}
            fullWidth
            placeholder="978-..."
          />
          <TextField
            label={t('textbooks.fields.language')}
            select
            value={language}
            onChange={(e) => setLanguage(e.target.value as 'es' | 'en' | 'pt' | 'other')}
            fullWidth
          >
            <MenuItem value="es">{t('textbooks.lang.es')}</MenuItem>
            <MenuItem value="en">{t('textbooks.lang.en')}</MenuItem>
            <MenuItem value="pt">{t('textbooks.lang.pt')}</MenuItem>
            <MenuItem value="other">{t('textbooks.lang.other')}</MenuItem>
          </TextField>
          <TextField
            label={t('textbooks.fields.notes')}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            multiline
            rows={3}
            fullWidth
          />
        </Stack>
        <Stack direction="row" spacing={1} mt={3} justifyContent="flex-end">
          <Button onClick={onClose}>{t('textbooks.actions.cancel')}</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving
              ? t('textbooks.actions.saving')
              : textbook
                ? t('textbooks.actions.saveChanges')
                : t('textbooks.actions.create')}
          </Button>
        </Stack>
      </Box>
    </Drawer>
  );
}
